import { ArrowUpRight, ExternalLink, FileAudio, FileVideo } from "lucide-react";
import { Section } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";

const host = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; } };

/** Outside pages with more history (admin: Innhald → Historie → Historielenker). Renders nothing when the list is empty. */
export const HistoryLinks = ({ links = [] }) => {
  const items = links.filter((l) => l?.url);
  if (!items.length) return null;
  return (
    <Section id="meir-historie" eyebrow="Meir historie" title="Andre som tek vare på historia" subtitle="Nettstader utanfor kulturhuset med bilete og forteljingar frå Sæbøvik og Halsnøy." tight>
      <ul className="grid gap-3 sm:grid-cols-2" data-testid="history-links">
        {items.map((l, i) => (
          <li key={`${l.url}-${i}`}>
            <FadeUp delay={i * 0.05}>
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="group flex items-start justify-between gap-4 rounded-[12px] border border-border bg-card p-5 transition-colors hover:border-foreground/30" data-testid={`history-link-${i}`}>
                <span className="min-w-0">
                  <span className="block font-display text-[18px] font-semibold leading-snug tracking-[-0.01em]">{l.label || host(l.url)}</span>
                  {l.note && <span className="mt-1.5 block text-[14px] leading-relaxed text-muted-foreground">{l.note}</span>}
                  <span className="t-mono-sm mt-2.5 block text-muted-foreground">{host(l.url)}</span>
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"><ArrowUpRight className="h-4 w-4" /></span>
              </a>
            </FadeUp>
          </li>
        ))}
      </ul>
    </Section>
  );
};

/** Recordings the board has added (admin: Innhald → Historie → Opptak). A direct audio/video file plays with the browser's own
 * player; a YouTube/Vimeo address is a plain outbound link – no iframe, no third-party script. Hidden while the list is empty. */
export const Recordings = ({ videos = [] }) => {
  const items = videos.filter((v) => v?.url && v?.title);
  if (!items.length) return null;
  return (
    <Section id="opptak" eyebrow="Lyd og video" title="Opptak frå arkivet" subtitle="Musikk og augneblinkar frå huset, tekne vare på av folk på øya." tight>
      <ul className="grid gap-5 sm:grid-cols-2" data-testid="history-recordings">
        {items.map((v, i) => (
          <li key={`${v.url}-${i}`} className="overflow-hidden rounded-[12px] border border-border bg-card" data-testid={`recording-${i}`}>
            {v.kind === "file" && v.media === "video" && <video controls preload="none" src={v.url} className="aspect-video w-full bg-ink" data-testid={`recording-${i}-video`}>Nettlesaren din kan ikkje spele denne videoen.</video>}
            <div className="p-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary">{v.media === "audio" ? <FileAudio className="h-4 w-4" /> : <FileVideo className="h-4 w-4" />}</span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-[18px] font-semibold leading-snug tracking-[-0.01em]">{v.title}{v.year && <span className="t-mono-sm ml-2 align-middle text-muted-foreground">{v.year}</span>}</h3>
                  {v.note && <p className="mt-1.5 text-[14px] leading-relaxed text-muted-foreground">{v.note}</p>}
                </div>
              </div>
              {v.kind === "file" && v.media === "audio" && <audio controls preload="none" src={v.url} className="mt-4 w-full" data-testid={`recording-${i}-audio`}>Nettlesaren din kan ikkje spele denne lydfila.</audio>}
              {v.kind === "link" && (
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="u-link mt-4 inline-flex items-center gap-1.5 text-[15px] font-medium" data-testid={`recording-${i}-link`}>
                  Opne opptaket på {host(v.url).includes("vimeo") ? "Vimeo" : "YouTube"} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
};
